import api from "./api";
import { Exercise } from "../types";

export interface ExerciseListFilters {
  only_mine?: boolean;
  muscle_groups?: string[];
  equipment?: string[];
}

export interface ExerciseListResponse {
  data: Exercise[];
  totalCount: number;
  hasNext: boolean;
  page: number;
  perPage: number;
}

async function list_all(
  professional_id: string,
  page = 1,
  per_page = 10,
  busca = "",
  only_mine = false
): Promise<Exercise[]> {
  const response = await list_all_paginated(professional_id, {
    page,
    per_page,
    busca,
    only_mine,
  });
  return response.data;
}

async function list_all_paginated(
  professional_id: string,
  options?: {
    page?: number;
    per_page?: number;
    busca?: string;
  } & ExerciseListFilters
): Promise<ExerciseListResponse> {
  try {
    const page = options?.page ?? 1;
    const per_page = options?.per_page ?? 10;
    const busca = options?.busca ?? "";
    const only_mine = options?.only_mine ?? false;
    const muscle_groups = options?.muscle_groups ?? [];
    const equipment = options?.equipment ?? [];
    const query = new URLSearchParams({
      page: String(page),
      per_page: String(per_page),
      search: busca,
    });
    if (only_mine) query.set("only_mine", "true");
    if (muscle_groups.length > 0) query.set("muscle_groups", muscle_groups.join(","));
    if (equipment.length > 0) query.set("equipment", equipment.join(","));

    const response = await api.get(
      `/exercicies/${professional_id}/?${query.toString()}`
    );
    const totalCount = Number(response.headers["x-total-count"] ?? response.data.length ?? 0);
    const hasNext = String(response.headers["x-has-next"] ?? "false") === "true";
    return {
      data: response.data ?? [],
      totalCount,
      hasNext,
      page: Number(response.headers["x-page"] ?? page),
      perPage: Number(response.headers["x-per-page"] ?? per_page),
    };
  } catch (e) {
    return {
      data: [],
      totalCount: 0,
      hasNext: false,
      page: options?.page ?? 1,
      perPage: options?.per_page ?? 10,
    };
  }
}

async function update(
  exercicie_id: string,
  body: Partial<Exercise>
): Promise<{ ok: boolean; error?: string }> {
  try {
    const payload = {
      name: body.name ?? "",
      description: body.description ?? "",
      image: body.image ?? "",
      video: body.video ?? "",
      aliases: body.aliases ?? [],
      muscle_groups: body.muscle_groups ?? body.area_muscular ?? [],
      equipment: body.equipment ?? [],
    };
    await api.put(`/exercicies/${exercicie_id}/`, payload);
    return { ok: true };
  } catch (e: unknown) {
    const message =
      (e as { response?: { data?: { error?: string } } })?.response?.data
        ?.error ?? "Erro ao atualizar o exercício.";
    return { ok: false, error: message };
  }
}

async function create(
  body: Omit<Exercise, "id" | "_id">
): Promise<{ ok: boolean; error?: string }> {
  try {
    const payload = {
      professional_id: body.professional_id,
      name: body.name ?? "",
      description: body.description ?? "",
      image: body.image ?? "",
      video: body.video ?? "",
      aliases: body.aliases ?? [],
      muscle_groups: body.muscle_groups ?? body.area_muscular ?? [],
      equipment: body.equipment ?? [],
    };
    await api.post(`/exercicies/`, payload);
    return { ok: true };
  } catch (e: unknown) {
    const message =
      (e as { response?: { data?: { error?: string } } })?.response?.data
        ?.error ?? "Erro ao criar o exercício.";
    return { ok: false, error: message };
  }
}

async function deleteItem(id: string) {
  try {
    await api.delete(`/exercicies/${id}/`);
    return true;
  } catch (e) {
    return false;
  }
}

const exercicies = { list_all, list_all_paginated, update, create, deleteItem };

export default exercicies;
